/**
 * Cloudflare Turnstile helper.
 *
 * Renders a single invisible widget once on first use, then resolves a fresh
 * single-use token on every `getTurnstileToken()` call. Concurrent calls are
 * serialized because the Turnstile widget can only produce one token at a time.
 */

interface TurnstileRenderParams {
  sitekey: string;
  size?: "normal" | "compact" | "flexible" | "invisible";
  appearance?: "always" | "execute" | "interaction-only";
  execution?: "render" | "execute";
  callback?: (token: string) => void;
  "error-callback"?: (errorCode?: string) => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
}

interface TurnstileApi {
  render: (
    container: HTMLElement | string,
    params: TurnstileRenderParams,
  ) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  ready: (cb: () => void) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_READY_TIMEOUT_MS = 10_000;
const TOKEN_TIMEOUT_MS = 30_000;

interface PendingRequest {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}

let widgetId: string | null = null;
let pendingChain: Promise<unknown> = Promise.resolve();
let activeRequest: PendingRequest | null = null;

const getSiteKey = (): string => {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  if (!siteKey) {
    throw new Error(
      "VITE_TURNSTILE_SITE_KEY is not set; cannot perform Turnstile verification.",
    );
  }
  return siteKey;
};

const waitForTurnstile = (): Promise<TurnstileApi> =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const deadline = Date.now() + SCRIPT_READY_TIMEOUT_MS;
    const intervalId = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(intervalId);
        resolve(window.turnstile);
      } else if (Date.now() > deadline) {
        window.clearInterval(intervalId);
        reject(new Error("Turnstile script failed to load."));
      }
    }, 50);
  });

const settleActive = (outcome: { token: string } | { error: Error }): void => {
  const request = activeRequest;
  activeRequest = null;
  if (!request) return;
  if ("token" in outcome) {
    request.resolve(outcome.token);
  } else {
    request.reject(outcome.error);
  }
};

const ensureWidget = async (): Promise<{ api: TurnstileApi; id: string }> => {
  const api = await waitForTurnstile();
  if (widgetId !== null) {
    return { api, id: widgetId };
  }

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.bottom = "0";
  container.style.right = "0";
  container.style.zIndex = "-1";
  container.style.pointerEvents = "none";
  container.style.visibility = "hidden";
  document.body.appendChild(container);

  widgetId = api.render(container, {
    sitekey: getSiteKey(),
    size: "invisible",
    appearance: "interaction-only",
    execution: "execute",
    callback: (token: string) => settleActive({ token }),
    "error-callback": (errorCode?: string) =>
      settleActive({
        error: new Error(`Turnstile error: ${errorCode ?? "unknown"}`),
      }),
    "expired-callback": () =>
      settleActive({ error: new Error("Turnstile token expired.") }),
    "timeout-callback": () =>
      settleActive({ error: new Error("Turnstile challenge timed out.") }),
  });
  return { api, id: widgetId };
};

const executeOnce = async (): Promise<string> => {
  const { api, id } = await ensureWidget();
  return await new Promise<string>((resolve, reject) => {
    let settled = false;

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      activeRequest = null;
      try {
        api.reset(id);
      } catch {
        // ignore - reset can fail if the widget is in an odd state
      }
      reject(new Error("Turnstile token request timed out."));
    }, TOKEN_TIMEOUT_MS);

    activeRequest = {
      resolve: (token: string) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        try {
          api.reset(id);
        } catch {
          // ignore
        }
        resolve(token);
      },
      reject: (err: Error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        try {
          api.reset(id);
        } catch {
          // ignore
        }
        reject(err);
      },
    };

    try {
      api.execute(id);
    } catch (err) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      activeRequest = null;
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
};

/**
 * Returns a fresh, single-use Cloudflare Turnstile token.
 * Concurrent callers are serialized so that the single widget instance is
 * reused without races.
 */
export const getTurnstileToken = async (): Promise<string> => {
  const next = pendingChain.then(executeOnce, executeOnce);
  pendingChain = next.catch(() => undefined);
  return await next;
};
